import { useCallback, useEffect, useState } from "react";
import {
    cancelAnaerobicEdits,
    confirmAnaerobicEdits,
    recalculateAnaerobicGraphs,
    updateAnaerobicThresholdValues,
} from "../../../api/anaerobic-threshold";
import {
    cancelInterpretation,
    confirmInterpretation,
} from "../../../api/interpretation";
import { getSmoothingRate, updateSmoothingRate } from "../../../api/smoothing";
import RefreshIcon from "../../../components/icons/RefreshIcon";
import AnaerobicGraphsGrid from "../../../components/interpretation/AnaerobicGraphsGrid";
import BottomBackToMain from "../../../components/interpretation/BottomBackToMain";
import QuickView from "../../../components/interpretation/QuickView";
import VATTables from "../../../components/interpretation/VATTables";
import SmoothingSlider from "../../../components/SmoothingSlider";
import { useInterpretationContext } from "../../../context/InterpretationContext";
import {
    useAnaerobicGraphs,
    useAnaerobicTablesData,
} from "../../../hooks/useAnaerobicData";
import usePrompt from "../../../hooks/usePrompt";
import useTestData from "../../../hooks/useTestData";
import { TestGraph } from "../../../types";
import { useAuth } from "../../../context/AuthContext";
import { GraphsProvider } from "../../../context/GraphsProvider";
import useTestId from "../../../hooks/useTestId";
import { ROUTES } from "../../config";
import { twMerge } from "tailwind-merge";

export default function AnaerobicThreshold() {
    const { isAllowed } = useAuth();
    const { value: testData, mutate: mutateTestData } = useTestData();
    const testNotCompleted = testData?.testStatus === "Not completed";

    const [smoothingValue, setSmoothingValue] = useState<number>(50);
    const [isEnlarged, setIsEnlarged] = useState<boolean>(false);
    const [isCompared, setIsCompared] = useState<boolean>(false);
    const [isEnabled, setIsEnabled] = useState(false);

    const [showRecalculateClickFeedback, setShowRecalculateClickFeedback] =
        useState(false);

    const { isInterpretationDirty } = useInterpretationContext();
    const testId = useTestId();

    const graphsValues = useAnaerobicGraphs({
        enabled: isEnabled,
    });
    const {
        graphs,
        mutate: mutateGraphs,
        setOriginalGraphsValue,
        unsavedData,
    } = graphsValues;
    const thereIsUnsavedData = unsavedData.length > 0;

    useEffect(() => {
        if (testId) {
            getSmoothingRate(testId).then((data) => {
                setSmoothingValue(data.vat);
                setIsEnabled(true);
            });
        }
    }, [testId]);

    const {
        value: tables,
        loading: tablesLoading,
        error: tablesError,
        refetch: refetchTables,
    } = useAnaerobicTablesData();

    const { nextPathname } = usePrompt({
        isDirty: thereIsUnsavedData,
        isInterpretationDirty,
        onConfirm: () => createConfirmFunction("confirm")(),
        onCancel: () => createConfirmFunction("cancel")(),
    });

    const createConfirmFunction = useCallback(
        (action: "confirm" | "cancel") => {
            return async () => {
                if (!testId) return;

                if (thereIsUnsavedData) {
                    if (action === "confirm") {
                        await confirmAnaerobicEdits(testId);
                    } else {
                        await cancelAnaerobicEdits(testId);
                    }
                }
                if (
                    isInterpretationDirty &&
                    !nextPathname.includes("interpretation")
                ) {
                    if (action === "confirm") {
                        await confirmInterpretation(testId);
                    } else {
                        await cancelInterpretation(testId);
                    }
                }
                const newGraphs = await mutateGraphs();
                setOriginalGraphsValue(newGraphs || null);
                refetchTables();
                mutateTestData();
            };
        },
        [testId, thereIsUnsavedData, isInterpretationDirty, nextPathname]
    );

    const updateGlobalValues = (data: TestGraph | null) => {
        if (!data) return;
        mutateGraphs((prevGraphs) => {
            if (!prevGraphs) return prevGraphs;
            return prevGraphs.map((graph) => {
                const _graph = { ...graph };
                const globalKeys = [
                    "vat_value",
                    "rcp_value",
                ] satisfies (keyof TestGraph)[];

                for (const key of globalKeys) {
                    if (key in _graph && key in data) {
                        _graph[key] = data[key];
                    }
                }
                return _graph;
            });
        }, false);
    };

    const onVatRelease = (vat: number, graph_id?: string) => {
        updateAnaerobicThresholdValues({
            testId,
            graph_id: graph_id ?? graphs?.[0].graph_id,
            vat_value: vat,
        }).then((data) => {
            updateGlobalValues(data);
            refetchTables();
        });
    };

    const onRCPRelease = (rcp: number) => {
        updateAnaerobicThresholdValues({
            testId,
            graph_id: graphs?.[1].graph_id,
            rcp_value: rcp,
        }).then((data) => {
            updateGlobalValues(data);
            refetchTables();
        });
    };

    const handleRecalculate = async () => {
        if (!testId) return;

        setShowRecalculateClickFeedback(true);
        setTimeout(() => {
            setShowRecalculateClickFeedback(false);
        }, 1000);
        const data = await recalculateAnaerobicGraphs({ testId });
        if (!data) return;
        mutateGraphs(data, false);
        refetchTables();
    };

    return (
        <GraphsProvider
            contextValue={{
                ...graphsValues,
                graphsType: "anaerobic-threshold",
                async refetchAdditionalData() {
                    await refetchTables();
                },
            }}
        >
            <div className="flex xl:pl-5 xl:pr-12 gap-6 pb-bottomPanel">
                <div className="flex flex-col gap-4 h-full w-leftSidebar shrink-0">
                    <QuickView />
                    <SmoothingSlider
                        value={smoothingValue}
                        setValue={setSmoothingValue}
                        onMouseRealease={async () => {
                            await updateSmoothingRate({
                                testId,
                                vat: smoothingValue,
                            });
                            mutateGraphs();
                        }}
                        disabled={!isAllowed("Anaerobic smoothing")}
                    />
                </div>
                <div className="h-full w-full min-w-0">
                    <div className="flex flex-col h-full w-full">
                        <div className="flex w-full mb-2 xl:mb- justify-between items-center">
                            <h1 className="text-blue-dark font-bold text-[22px] leading-9">
                                {
                                    ROUTES.INTERPRETATION.subroutes
                                        .ANAEROBIC_THRESHOLD.label
                                }
                            </h1>
                            {isAllowed("Anaerobic RCP") &&
                                isAllowed("Anaerobic VAT") && (
                                    <button
                                        className={twMerge(
                                            "flex gap-1 h-10 px-3 text-blue-dark items-center border-2 border-blue-dark rounded-full transition-colors",
                                            showRecalculateClickFeedback &&
                                                "bg-blue-dark2/15"
                                        )}
                                        onClick={handleRecalculate}
                                    >
                                        <RefreshIcon
                                            className={twMerge(
                                                "stroke-blue-dark transition-transform",
                                                showRecalculateClickFeedback &&
                                                    "animate-spin"
                                            )}
                                        />
                                        Recalculate
                                    </button>
                                )}
                        </div>
                        <div className="flex flex-row justify-between gap-6 pb-4">
                            <div
                                className={`grow min-w-0 ${
                                    isEnlarged || isCompared ? "mt-[28px]" : ""
                                }`}
                            >
                                <AnaerobicGraphsGrid
                                    onVatRelease={onVatRelease}
                                    onRCPRelease={onRCPRelease}
                                    updateIsEnlarged={setIsEnlarged}
                                    updateIsCompared={setIsCompared}
                                />
                            </div>
                            <VATTables
                                tablesData={tables}
                                loading={tablesLoading}
                                error={tablesError}
                            />
                        </div>
                    </div>
                    <BottomBackToMain
                        isFixed
                        href={
                            testNotCompleted
                                ? `/test/${testId}/${ROUTES.INTERPRETATION.path}/${ROUTES.INTERPRETATION.subroutes.SUMMARY_TABLE.path}`
                                : undefined
                        }
                        label={testNotCompleted ? "Confirm" : undefined}
                        backButtonSettings={
                            testNotCompleted
                                ? {
                                      href: `/test/${testId}/${ROUTES.DATA_INPUT.path}`,
                                  }
                                : undefined
                        }
                    />
                </div>
            </div>
        </GraphsProvider>
    );
}
